export const ASSIGNABLE_ROLES = ["admin", "recruiter", "document_specialist", "observer"] as const;

export type AssignableRole = (typeof ASSIGNABLE_ROLES)[number];

export const PERMISSION_KEYS = [
  "view",
  "manage_users",
  "manage_vacancies",
  "manage_candidates",
  "manage_stages",
  "manage_interviews",
  "manage_tasks",
  "manage_documents",
  "view_reports",
  "manage_settings",
  "qualify",
  "decision",
  "compliance",
  "upload_documents",
] as const;

export type PermissionKey = (typeof PERMISSION_KEYS)[number];

export const ROLE_PERMISSIONS: Record<string, readonly (PermissionKey | "*")[]> = {
  system_admin: ["*"],
  admin: [...PERMISSION_KEYS],
  company_admin: [...PERMISSION_KEYS],
  recruiter: ["view", "manage_vacancies", "manage_candidates", "manage_interviews", "manage_tasks", "upload_documents", "qualify", "decision"],
  document_specialist: ["view", "manage_documents", "upload_documents", "compliance", "decision"],
  observer: ["view", "view_reports"],
};

const permissionKeys = new Set<string>(PERMISSION_KEYS);

export function defaultPermissions(role: string): PermissionKey[] {
  return (ROLE_PERMISSIONS[role] ?? []).filter((permission): permission is PermissionKey => permission !== "*");
}

export function resolvePermissions(role: string, assigned?: string | readonly string[] | null): (PermissionKey | "*")[] {
  if (role === "system_admin") return ["*"];
  if (assigned === null || assigned === undefined) return defaultPermissions(role);

  let parsed: unknown = assigned;
  if (typeof assigned === "string") {
    try { parsed = JSON.parse(assigned); }
    catch { return []; }
  }
  if (!Array.isArray(parsed)) return [];
  return [...new Set(parsed.filter((permission): permission is PermissionKey => typeof permission === "string" && permissionKeys.has(permission)))];
}

export function hasPermission(permissions: readonly string[] | undefined, permission: PermissionKey): boolean {
  return Boolean(permissions?.includes("*") || permissions?.includes(permission));
}

/**
 * The permission model above is what the routes enforce; it is not what a human
 * should have to configure. Fourteen switches asked the admin to know that a
 * recruiter needs `qualify` *and* `decision` *and* `upload_documents`, and every
 * account created through that screen was one forgotten toggle away from a
 * half-working role. The dialog now edits these five groups instead.
 */
export const PERMISSION_GROUPS = [
  { key: "recruiting", primary: "manage_candidates", permissions: ["manage_candidates", "manage_vacancies", "manage_interviews", "manage_tasks", "qualify"] },
  { key: "documents", primary: "manage_documents", permissions: ["manage_documents", "compliance"] },
  { key: "reports", primary: "view_reports", permissions: ["view_reports"] },
  { key: "settings", primary: "manage_settings", permissions: ["manage_settings", "manage_stages"] },
  { key: "team", primary: "manage_users", permissions: ["manage_users"] },
] as const satisfies readonly { key: string; primary: PermissionKey; permissions: readonly PermissionKey[] }[];

export type PermissionGroupKey = (typeof PERMISSION_GROUPS)[number]["key"];

export const PERMISSION_GROUP_KEYS = PERMISSION_GROUPS.map((group) => group.key) as PermissionGroupKey[];

/** Held by every account that can sign in: a switch that only ever means "this
 * user cannot use the product" is not a permission, it is the `blocked` status. */
const IMPLICIT_PERMISSIONS: PermissionKey[] = ["view"];

/** Attaching a file to a candidate you already work on, and recording the
 * outcome of that work, are consequences of the two content groups rather than
 * boundaries of their own — every role that had one had the other. */
const DERIVED_PERMISSIONS: PermissionKey[] = ["upload_documents", "decision"];

const CONTENT_GROUPS: PermissionGroupKey[] = ["recruiting", "documents"];

export function groupsFromPermissions(permissions: readonly string[] | undefined): PermissionGroupKey[] {
  if (permissions?.includes("*")) return [...PERMISSION_GROUP_KEYS];
  // Keyed off one telling permission rather than "holds any of them", so a
  // document specialist's `decision` does not light up the recruiting group.
  return PERMISSION_GROUPS.filter((group) => permissions?.includes(group.primary)).map((group) => group.key);
}

export function permissionsFromGroups(groups: readonly PermissionGroupKey[]): PermissionKey[] {
  const selected = new Set<PermissionKey>(IMPLICIT_PERMISSIONS);
  for (const group of PERMISSION_GROUPS)
    if (groups.includes(group.key)) for (const permission of group.permissions) selected.add(permission);
  if (CONTENT_GROUPS.some((group) => groups.includes(group)))
    for (const permission of DERIVED_PERMISSIONS) selected.add(permission);
  return PERMISSION_KEYS.filter((permission) => selected.has(permission));
}

export const defaultGroups = (role: string): PermissionGroupKey[] => groupsFromPermissions(defaultPermissions(role));

export function roleFromGroups(groups: readonly PermissionGroupKey[]): AssignableRole {
  if (groups.includes("team") || groups.includes("settings")) return "admin";
  if (groups.includes("recruiting")) return "recruiter";
  if (groups.includes("documents")) return "document_specialist";
  return "observer";
}
